import type { Customer } from '@/types';

import { getConsent } from './consent';
import { initConsentMode } from './consentMode';
import { injectCustomTrackingAllPages } from './customTracking';
import { pushToDataLayer } from './dataLayer';
import { initFBPixel, trackFBEvent } from './fbPixel';
import { initGA4, trackPageView } from './gtag';
import { initGTM } from './gtm';

let initialized = false;

/**
 * Initialize all analytics based on customer configuration.
 * Must be called after generalStore.fetchGeneral() resolves so that
 * customer data is available.
 *
 * @param customer - Customer settings from the general store.
 */
export function initAnalytics(customer: Customer): void {
  if (initialized) return;
  initialized = true;

  // Step 1: Set up Consent Mode defaults (must happen first)
  initConsentMode();

  // Step 2: Load scripts based on customer config
  const ga4Id = customer.google_analytics_trackingid_ng;
  const gtmId = customer.google_tag_manager_trackingid;
  const fbPixelId = customer.fb_pixel_events_id;
  const fbPixelEnabled = customer.fb_pixel_events;
  const customAllPages = customer.custom_tracking_allpages;

  // GA4 — uses Advanced Consent Mode (loads immediately, cookies suppressed until consent)
  if (ga4Id) {
    initGA4(ga4Id);
  }

  // GTM — also uses Consent Mode natively
  if (gtmId) {
    initGTM(gtmId);
  }

  // FB Pixel — strict gating (only loads after marketing consent)
  if (fbPixelId && fbPixelEnabled) {
    initFBPixel(fbPixelId);
  }

  // Custom tracking scripts — gated behind statistics consent
  if (customAllPages) {
    injectCustomTrackingAllPages(customAllPages);
  }
}

/**
 * Track a page view across all active analytics platforms.
 * Called from router.afterEach().
 */
export function trackAllPageView(path: string, title: string): void {
  if (!initialized) return;

  // GA4 page view
  trackPageView(path, title);

  // GTM dataLayer: pageView (backward compatible with old GTM triggers)
  pushToDataLayer({
    event: 'pageView',
    virtualUrl: window.location.href,
  });

  // FB Pixel PageView (only if marketing consent is active)
  const consent = getConsent();
  if (consent?.marketing) {
    trackFBEvent('PageView');
  }
}

// Re-export ecommerce functions for convenience
export { trackViewItem, trackProductClick, trackAddToCart, trackRemoveFromCart, trackBeginCheckout, trackCheckoutStep2, trackPurchase } from './ecommerce';
export { injectCustomTrackingReceipt } from './customTracking';
